import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import api from "@/lib/api";
import {
  HardHat, MapPin, Fuel, Send, QrCode, Loader2, X, Plus,
  Activity, CheckCircle, Clock, Droplet, Truck, Settings,
  Sparkles, Radio, Wrench, ArrowLeft, LogOut, Hand, PlusCircle,
  Zap, ChevronRight, Settings2, Trash2
} from "lucide-react";

export default function MachinistDashboard() {
  const { session, hubData, refreshData, logout } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const nav = useNavigate();
  const { sites, machines, drivers, requests } = hubData;
  const myName = session?.userName || "Machinist";

  const [activeSiteId, setActiveSiteId] = useState(null);
  const [activeMachine, setActiveMachine] = useState(null);
  const [fuelLevel, setFuelLevel] = useState(50);
  const [needsAdBlue, setNeedsAdBlue] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [toast, setToast] = useState(null);
  const [statusMachine, setStatusMachine] = useState(null);
  const [statusType, setStatusType] = useState("NORMAL");
  const [statusNotes, setStatusNotes] = useState("");
  const [showExternal, setShowExternal] = useState(false);
  const [extType, setExtType] = useState("CRANE");
  const [extId, setExtId] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };
  const activeSite = sites.find(s => s.id === activeSiteId);
  const siteMachines = useMemo(() => machines.filter(m => m.site_id === activeSiteId), [machines, activeSiteId]);

  const myRecentRequests = useMemo(() =>
    requests.filter(r => r.requester_type === "MACHINIST" || r.requester_type === "machinist")
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  , [requests]);

  const handleQRScan = () => {
    if (siteMachines.length === 0) return;
    const target = siteMachines[Math.floor(Math.random() * siteMachines.length)];
    setActiveMachine(target);
    setFuelLevel(target.fuel_level_percent || 50);
    setNeedsAdBlue(target.needs_adblue || false);
    setIsRequesting(true);
  };

  const handleFuelRequest = async () => {
    if (!activeMachine || !activeSite) return;
    await api.createRequest({
      hub_id: session.hubId, machine_id: activeMachine.id,
      machine_number: activeMachine.machine_number, site_id: activeSite.id,
      site_name: activeSite.name,
      urgency: fuelLevel < 20 ? "URGENT" : "NONE",
      products: needsAdBlue ? ["DIESEL", "ADBLUE"] : ["DIESEL"],
      notes: `Requested by machinist ${myName}`,
      requester_type: "MACHINIST",
      current_fuel_level: fuelLevel
    });
    showToast(t("fuel_request") + " sent!");
    setActiveMachine(null);
    setIsRequesting(false);
    refreshData();
  };

  const handleStatusSubmit = async () => {
    if (!statusMachine) return;
    await api.updateMachine(statusMachine.id, {
      status_type: statusType, status_notes: statusNotes
    });
    await api.createEvent({
      hub_id: session.hubId, event_type: "MACHINE_STATUS_CHANGE",
      message: `${statusMachine.machine_number} status: ${statusType} - ${statusNotes}`,
      machine_id: statusMachine.id, machine_number: statusMachine.machine_number
    });
    showToast(t("action_center") + " updated!");
    setStatusMachine(null);
    setStatusNotes("");
    refreshData();
  };

  const handleRegisterExternal = async () => {
    if (!activeSiteId || !extId) return;
    await api.createMachine({
      hub_id: session.hubId, machine_number: `EXT-${extId.toUpperCase()}`,
      name: `${extType} ${extId}`, site_id: activeSiteId,
      is_external: true, external_type: extType, is_rental: true,
      fuel_level_percent: 50, needs_adblue: false
    });
    showToast(t("confirm_import") + "!");
    setExtId("");
    setShowExternal(false);
    refreshData();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "DEFECT": return "bg-red-600 text-white";
      case "TRANSPORT": return "bg-blue-600 text-white";
      case "UPKEEP": return "bg-purple-600 text-white";
      default: return "bg-lime-500/10 text-lime-400";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "DEFECT": return <Wrench className="w-3 h-3" />;
      case "TRANSPORT": return <Truck className="w-3 h-3" />;
      case "UPKEEP": return <Settings className="w-3 h-3" />;
      default: return <CheckCircle className="w-3 h-3" />;
    }
  };

  // Site selection screen
  if (!activeSiteId) {
    return (
      <div className="min-h-[100dvh] bg-slate-950 flex flex-col items-center justify-center p-6" data-testid="site-selection">
        <div className="max-w-md w-full space-y-8 animate-fadeInUp">
          <div className="text-center">
            <div className="w-20 h-20 bg-lime-500 rounded-md flex items-center justify-center mx-auto mb-4 shadow-[0_0_30px_rgba(132,204,22,0.3)]">
              <MapPin className="w-10 h-10 text-slate-950" />
            </div>
            <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-100">{t("select_site")}</h2>
            <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-2">{myName} / {session?.hubName}</p>
          </div>
          <div className="grid gap-3">
            {sites.length === 0 ? (
              <div className="p-10 border border-dashed border-slate-800 rounded-md text-center">
                <p className="text-xs text-slate-600 font-bold uppercase tracking-wider">{t("no_data")}</p>
              </div>
            ) : sites.map((site, i) => (
              <button key={site.id} data-testid={`select-site-${site.id}`}
                onClick={() => setActiveSiteId(site.id)}
                className={`group w-full p-5 bg-slate-900 border border-slate-800 rounded-md hover:border-lime-500/40 transition-all text-left flex items-center justify-between active:scale-[0.98] animate-fadeInUp stagger-${i + 1}`}
                style={{ opacity: 0 }}>
                <div>
                  <h3 className="text-sm font-bold text-slate-100 uppercase tracking-tight group-hover:text-lime-400 transition-colors">{site.name}</h3>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-1">{site.city} / {site.code}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-lime-500 group-hover:translate-x-1 transition-all" />
              </button>
            ))}
          </div>
          <button onClick={() => { logout(); nav("/"); }}
            className="w-full py-3 bg-slate-900 border border-slate-800 rounded-md text-xs font-bold text-slate-400 uppercase tracking-wider hover:text-white transition-all flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" /> {t("back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto min-h-[100dvh] bg-slate-950 pb-32 relative" data-testid="machinist-dashboard">
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 left-4 right-4 z-[1000] bg-lime-500 text-slate-950 px-5 py-3 rounded-md shadow-lg flex items-center gap-2 font-bold text-sm uppercase tracking-wider animate-slideInDown max-w-lg mx-auto">
          <Sparkles className="w-4 h-4" /> {toast}
        </div>
      )}

      {/* Fuel Request Modal */}
      {activeMachine && isRequesting && (
        <div className="fixed inset-0 z-[400] bg-slate-950/95 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-slate-900 rounded-md w-full max-w-sm border border-slate-800 animate-scaleIn">
            <div className="bg-slate-950 p-5 flex justify-between items-center border-b border-slate-800">
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight text-slate-100">{t("fuel_request")}</h3>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-1">{activeMachine.machine_number} / {activeMachine.name}</p>
              </div>
              <button onClick={() => setIsRequesting(false)} className="p-2 bg-slate-800 rounded-md text-slate-400" data-testid="close-fuel-modal"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <div className="flex justify-between items-end mb-2">
                  <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{t("tank_level")}</span>
                  <span className="text-3xl font-black text-slate-100 font-mono">{fuelLevel}%</span>
                </div>
                <input data-testid="fuel-slider" type="range" min="0" max="100" step="5" value={fuelLevel}
                  onChange={e => setFuelLevel(parseInt(e.target.value))}
                  className="w-full h-3 bg-slate-800 rounded-full appearance-none cursor-pointer accent-lime-500" />
                {fuelLevel < 20 && <p className="text-[10px] text-red-400 font-bold uppercase tracking-wider mt-2 animate-pulse">LOW FUEL - URGENT REQUEST</p>}
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-950 border border-slate-800 rounded-md">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-md flex items-center justify-center ${needsAdBlue ? "bg-sky-500/20 text-sky-400" : "bg-slate-800 text-slate-500"}`}>
                    <Droplet className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-bold text-slate-200 uppercase">{t("adblue_needed")}</span>
                </div>
                <button data-testid="adblue-toggle" onClick={() => setNeedsAdBlue(!needsAdBlue)}
                  className={`w-14 h-7 rounded-full transition-all relative ${needsAdBlue ? "bg-sky-500" : "bg-slate-700"}`}>
                  <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full transition-all shadow ${needsAdBlue ? "left-7" : "left-0.5"}`} />
                </button>
              </div>
              <button data-testid="send-fuel-request" onClick={handleFuelRequest}
                className="btn-industrial w-full py-4 bg-lime-500 text-slate-950 rounded-md text-sm font-black shadow-[0_0_20px_rgba(132,204,22,0.3)]">
                <Send className="w-5 h-5" /> {t("fuel_request")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Modal */}
      {statusMachine && (
        <div className="fixed inset-0 z-[400] bg-slate-950/95 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-slate-900 rounded-md w-full max-w-sm border border-slate-800 animate-scaleIn">
            <div className="bg-slate-950 p-5 flex justify-between items-center border-b border-slate-800">
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight text-slate-100">{t("action_center")}</h3>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-1">{statusMachine.machine_number}</p>
              </div>
              <button onClick={() => setStatusMachine(null)} className="p-2 bg-slate-800 rounded-md text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: "NORMAL", label: t("in_use"), icon: <CheckCircle className="w-3.5 h-3.5" />, c: "lime" },
                  { id: "DEFECT", label: t("defect"), icon: <Wrench className="w-3.5 h-3.5" />, c: "red" },
                  { id: "UPKEEP", label: t("maintenance"), icon: <Settings className="w-3.5 h-3.5" />, c: "purple" },
                  { id: "TRANSPORT", label: t("transport_needed"), icon: <Truck className="w-3.5 h-3.5" />, c: "blue" }
                ].map(opt => (
                  <button key={opt.id} data-testid={`status-${opt.id}`} onClick={() => setStatusType(opt.id)}
                    className={`p-3 rounded-md text-[10px] font-bold uppercase border flex items-center gap-2 transition-all ${statusType === opt.id ? `border-${opt.c}-500 bg-${opt.c}-500/10 text-${opt.c}-400` : "border-slate-800 bg-slate-950 text-slate-500"}`}>
                    {opt.icon} {opt.label}
                  </button>
                ))}
              </div>
              <textarea data-testid="status-notes" value={statusNotes} onChange={e => setStatusNotes(e.target.value)}
                placeholder={t("details_reason")}
                className="w-full p-4 bg-slate-950 border border-slate-800 rounded-md text-xs text-slate-100 h-20 resize-none outline-none focus:border-lime-500/40 placeholder:text-slate-600" />
              <button data-testid="submit-status" onClick={handleStatusSubmit}
                className="btn-industrial w-full py-4 bg-lime-500 text-slate-950 rounded-md text-sm font-black">
                <Send className="w-4 h-4" /> {t("send_to_planner")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* External Machine Modal */}
      {showExternal && (
        <div className="fixed inset-0 z-[450] bg-slate-950/95 flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-slate-900 rounded-md w-full max-w-sm border border-slate-800 animate-scaleIn">
            <div className="bg-purple-600 p-5 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-black uppercase tracking-tight text-white">{t("add_rental")}</h3>
                <p className="text-[10px] text-purple-200 font-bold uppercase tracking-wider mt-1">New machine registration</p>
              </div>
              <button onClick={() => setShowExternal(false)} className="p-2 hover:bg-white/10 rounded-md text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">{t("machine_type")}</label>
                <div className="grid grid-cols-2 gap-2">
                  {["CRANE", "GENERATOR", "TANK", "OTHER"].map(type => (
                    <button key={type} onClick={() => setExtType(type)}
                      className={`p-3 rounded-md text-[10px] font-bold uppercase border transition-all ${extType === type ? "border-purple-500 bg-purple-500/10 text-purple-400" : "border-slate-800 bg-slate-950 text-slate-500"}`}>
                      {type}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">{t("fleet_id")}</label>
                <input data-testid="ext-id-input" value={extId} onChange={e => setExtId(e.target.value)}
                  placeholder="e.g. CAT D6R"
                  className="w-full p-4 bg-slate-950 border border-slate-800 rounded-md font-bold text-slate-100 outline-none focus:border-purple-500/50 placeholder:text-slate-600" />
              </div>
              <button data-testid="register-ext" onClick={handleRegisterExternal} disabled={!extId}
                className="btn-industrial w-full py-4 bg-purple-600 text-white rounded-md text-sm font-black disabled:opacity-30">
                <PlusCircle className="w-5 h-5" /> {t("confirm_import")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-slate-950 p-4 border-b border-slate-800 sticky top-0 z-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-500 rounded-md flex items-center justify-center text-slate-950 shadow-[0_0_15px_rgba(245,158,11,0.3)]">
            <HardHat className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-100">{t("machinist_dashboard")}</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500">{myName} / {activeSite?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setActiveSiteId(null)} className="p-2.5 bg-slate-800 border border-slate-700 rounded-md text-slate-400 hover:text-white transition-all">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button onClick={() => { logout(); nav("/"); }} data-testid="machinist-exit"
            className="p-2.5 bg-red-600/10 border border-red-600/20 rounded-md text-red-500 hover:bg-red-600 hover:text-white transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="p-4 space-y-5">
        {/* Site Selector */}
        <div className="bg-slate-900 border border-slate-800 rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-red-500" /> {t("current_site")}
            </span>
          </div>
          <select data-testid="site-selector" value={activeSiteId || ""} onChange={e => setActiveSiteId(e.target.value)}
            className="w-full p-3 bg-slate-950 border border-slate-800 rounded-md font-bold text-slate-100 outline-none focus:border-lime-500/40">
            {sites.map(s => <option key={s.id} value={s.id}>{s.name} / {s.city}</option>)}
          </select>
        </div>

        {/* Machine Pool */}
        <div>
          <div className="flex items-center justify-between mb-3 px-1">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2">
              <Activity className="w-3.5 h-3.5 text-lime-500" /> {t("machine_pool")}
            </span>
            <button data-testid="add-rental-btn" onClick={() => setShowExternal(true)}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-[10px] font-bold uppercase flex items-center gap-1 active:scale-95 transition-all">
              <PlusCircle className="w-3.5 h-3.5" /> Rental
            </button>
          </div>
          <div className="space-y-3">
            {siteMachines.map(m => {
              const hasReq = requests.some(r => r.machine_id === m.id && r.status !== "DONE");
              return (
                <div key={m.id} data-testid={`machine-${m.id}`}
                  className={`bg-slate-900 border rounded-md p-4 flex items-center justify-between transition-all ${m.is_external ? "border-purple-500/30" : "border-slate-800"}`}>
                  <div className="flex items-center gap-3">
                    <button onClick={() => { setActiveMachine(m); setFuelLevel(m.fuel_level_percent || 50); setNeedsAdBlue(m.needs_adblue || false); setIsRequesting(true); }}
                      className={`w-12 h-12 rounded-md flex items-center justify-center text-white font-bold cursor-pointer hover:scale-105 transition-all ${m.is_external ? "bg-purple-600" : hasReq ? "bg-lime-500 text-slate-950" : "bg-slate-700"}`}>
                      {m.is_external ? <Zap className="w-5 h-5" /> : <HardHat className="w-5 h-5" />}
                    </button>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-slate-100 uppercase tracking-tight">{m.machine_number}</span>
                        {m.is_external && <span className="text-[8px] font-bold bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">RENTAL</span>}
                      </div>
                      <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{m.name}</p>
                      <div className="flex gap-1 mt-1">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5 ${getStatusColor(m.status_type)}`}>
                          {getStatusIcon(m.status_type)} {m.status_type || "OK"}
                        </span>
                        {hasReq && <span className="text-[9px] font-bold bg-lime-500/10 text-lime-400 px-1.5 py-0.5 rounded flex items-center gap-0.5 animate-pulse"><Clock className="w-2.5 h-2.5" /> QUEUED</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => { setStatusMachine(m); setStatusType(m.status_type || "NORMAL"); setStatusNotes(m.status_notes || ""); }}
                      className="p-2.5 bg-slate-950 border border-slate-800 rounded-md text-slate-400 hover:text-white transition-all">
                      <Settings2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setActiveMachine(m); setFuelLevel(m.fuel_level_percent || 50); setNeedsAdBlue(m.needs_adblue || false); setIsRequesting(true); }}
                      className={`p-2.5 rounded-md transition-all font-bold ${m.is_external ? "bg-purple-600 text-white" : "bg-lime-500 text-slate-950"} active:scale-95`}>
                      <Fuel className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Request Tracker */}
        {myRecentRequests.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-md p-4" data-testid="echo-tracker">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest flex items-center gap-2 mb-3">
              <Radio className="w-3.5 h-3.5 text-lime-500" /> {t("echo_tracker")}
            </h3>
            <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
              {myRecentRequests.slice(0, 6).map(req => (
                <div key={req.id} className={`min-w-[160px] p-4 rounded-md border shrink-0 ${req.status === "DONE" ? "border-lime-500/30 bg-lime-500/5" : "border-slate-800 bg-slate-950"}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-black text-slate-100">{req.machine_number}</span>
                    <div className={`w-2.5 h-2.5 rounded-full ${req.status === "DONE" ? "bg-lime-500" : "bg-red-500 animate-pulse"}`} />
                  </div>
                  <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wider truncate">{req.site_name}</p>
                  {req.status === "DONE" ? (
                    <div className="mt-2 p-2 bg-lime-500 text-slate-950 rounded text-[10px] font-black text-center">
                      +{req.delivered_diesel || 0}L
                    </div>
                  ) : (
                    <p className="text-[10px] text-amber-400 font-bold uppercase mt-2">{t("waiting")}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Floating QR Scan Button */}
      <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[100]">
        <button data-testid="qr-scan-btn" onClick={() => { setIsScanning(true); setTimeout(() => { setIsScanning(false); handleQRScan(); }, 800); }}
          disabled={isScanning}
          className="btn-industrial px-8 py-4 bg-slate-900 text-white rounded-full shadow-[0_10px_40px_rgba(0,0,0,0.6)] border border-slate-700 hover:border-lime-500/40">
          {isScanning ? <Loader2 className="w-5 h-5 animate-spin" /> : <QrCode className="w-5 h-5 text-lime-500" />}
          <span className="text-xs">{isScanning ? "Scanning..." : "Quick Scan"}</span>
        </button>
      </div>
    </div>
  );
}
